"use client";

import { ArrowRight } from "lucide-react";
import { motion } from "motion/react";
import { type FocusEvent, type PointerEvent, useState } from "react";

import { EASE_OUT_EXPO, Reveal } from "@/components/motion/reveal";
import { TextRoll } from "@/components/motion/text-roll";
import { Image } from "@/components/shared/image";
import { Link } from "@/i18n/navigation";
import type { MediaAsset } from "@/lib/media";

// =============================================================================

type NextProjectProps = {
  label: string;
  project: { slug: string; name: string; image: MediaAsset | null };
};

/**
 * Link to the next project closing the page: its name rolls letter by letter
 * and a preview of its cover unfolds on hover.
 */
export function NextProject({ label, project }: NextProjectProps) {
  const [isActive, setIsActive] = useState(false);
  const state = isActive ? "hover" : "rest";

  const onPointerEnter = (event: PointerEvent) => {
    if (event.pointerType === "mouse") setIsActive(true);
  };
  const onFocus = (event: FocusEvent<HTMLAnchorElement>) => {
    if (event.currentTarget.matches(":focus-visible")) setIsActive(true);
  };

  return (
    <Reveal y={48}>
      <motion.div initial="rest" animate={state}>
        <Link
          href={`/projects/${project.slug}`}
          onPointerEnter={onPointerEnter}
          onPointerLeave={() => setIsActive(false)}
          onFocus={onFocus}
          onBlur={() => setIsActive(false)}
          className="group relative block border-t border-line pt-8 pb-[clamp(4rem,9vw,8rem)] sm:pt-10"
        >
          <span className="flex items-center gap-3 text-sm tracking-[0.2em] text-muted-foreground uppercase">
            <span aria-hidden className="size-2 bg-brand" />
            {label}
          </span>

          <span className="mt-6 flex items-end justify-between gap-6 sm:mt-8">
            {/* Rolling letters cannot wrap: plain text on small screens */}
            <span className="min-w-0 text-title wrap-break-word lg:hidden">
              {project.name}
            </span>
            <TextRoll
              className="hidden text-title lg:inline-flex"
              stagger={0.012}
            >
              {project.name}
            </TextRoll>
            <span className="mb-[0.4em] grid size-12 shrink-0 place-items-center rounded-full border border-foreground/15 transition-transform duration-700 ease-out-expo group-hover:-rotate-45 sm:size-16">
              <ArrowRight className="size-5 sm:size-6" />
            </span>
          </span>

          {/* Cover preview unfolding above the arrow */}
          {project.image && (
            <motion.span
              aria-hidden
              className="pointer-events-none absolute right-[clamp(4.5rem,8vw,7rem)] bottom-[clamp(4rem,9vw,8rem)] hidden aspect-video w-[clamp(14rem,22vw,20rem)] overflow-hidden rounded-xl shadow-2xl lg:block"
              variants={{
                rest: {
                  clipPath: "inset(100% 0% 0% 0% round 0.75rem)",
                  y: 24,
                  rotate: 4,
                },
                hover: {
                  clipPath: "inset(0% 0% 0% 0% round 0.75rem)",
                  y: 0,
                  rotate: -3,
                },
              }}
              transition={{ duration: 0.8, ease: EASE_OUT_EXPO }}
            >
              <motion.span
                className="absolute inset-0"
                variants={{ rest: { scale: 1.3 }, hover: { scale: 1 } }}
                transition={{ duration: 1.1, ease: EASE_OUT_EXPO }}
              >
                <Image
                  src={project.image.url}
                  alt=""
                  fill
                  sizes="20rem"
                  className="object-cover object-top"
                />
              </motion.span>
            </motion.span>
          )}
        </Link>
      </motion.div>
    </Reveal>
  );
}

// =============================================================================
